import { styled, alpha } from "@mui/material/styles";
import { IconButton, Avatar } from "@mui/material";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LogoutIcon from "@mui/icons-material/Logout";
import GitHubIcon from "@mui/icons-material/GitHub";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RadarIcon from "@mui/icons-material/Radar";
import BarChartIcon from "@mui/icons-material/BarChart";
import Switch from "@mui/material/Switch";
import { MouseEvent, useEffect, useState, useRef } from "react";
import {
  deleteDiagramFile,
  invalidateToken,
  saveFileToGithub,
} from "../../../Api/ServerInterface";
import useStore from "../../../Store/Store";
import { clear } from "idb-keyval";
import { removeURLParam } from "../../../utils/URLUtils";
import workflowFile from "../../../utils/CodeCanvasWorkflowFile.txt";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import { login } from "../../../utils/AuthUtils";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import FirebaseAuth from "../../Common/FirebaseAuth/FirebaseAuth";
import { theme } from "../../../Themes/Themes";
import { createPortal } from "react-dom";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  zIndex: 10000000, // Extremely high z-index to appear on top of everything
  "& .MuiPaper-root": {
    borderRadius: 15,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.custom.lightGrey,
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.secondary.main,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

export default function OptionsMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [doesCodeCanvasWorkflowFileExist, setDoesCodeCanvasWorkflowFileExist] =
    useState(false);
  const [telemetryData, setTelemetryData] = useState(
    localStorage.getItem("telemetryData") !== "false"
  );
  const firebaseAuthRef = useRef<HTMLDivElement>(null);
  const optionsIconRef = useRef<HTMLDivElement>(null);
  const [optionsIconPosition, setOptionsIconPosition] = useState({
    top: 0,
    right: 0,
  });

  // Firebase auth hook
  const { user: firebaseUser, signOutUser } = useFirebaseAuth();

  const {
    isLoggedIn,
    debugMode,
    repoData,
    setLoggedIn,
    postToDrawioWaitForResponse,
    setErrorNotification,
    setSuccessNotification,
    statsWidgetVisible,
    setStatsWidgetVisible,
    isRunningSimulation,
    selectedSimulationKey,
    isMaxSD,
    showLandingComponent,
  } = useStore((state) => ({
    isLoggedIn: state.session.isLoggedIn,
    debugMode: state.debugMode,
    repoData: state.repoData,
    setLoggedIn: state.setLoggedIn,
    postToDrawioWaitForResponse: state.postToDrawioWaitForResponse,
    setErrorNotification: state.setErrorNotification,
    setSuccessNotification: state.setSuccessNotification,
    statsWidgetVisible: state.statsWidgetVisible,
    setStatsWidgetVisible: state.setStatsWidgetVisible,
    isRunningSimulation: state.isRunningSimulation,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    isMaxSD: state.isMaxSD,
    showLandingComponent: state.showLandingComponent,
  }));

  // Hide button when SD is maximized AND running simulation, or when landing page is showing
  const shouldHideButton =
    (isMaxSD && isRunningSimulation) || showLandingComponent;

  // Apply smaller styling when SD is maximized
  const shouldApplySmallStyling = isMaxSD;

  useEffect(() => {
    const updateOptionsIconPosition = () => {
      if (optionsIconRef.current) {
        const rect = optionsIconRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        setOptionsIconPosition({
          top: rect.top,
          right: viewportWidth - rect.right,
        });
      }
    };

    updateOptionsIconPosition();
    window.addEventListener("resize", updateOptionsIconPosition);
    window.addEventListener("scroll", updateOptionsIconPosition);

    return () => {
      window.removeEventListener("resize", updateOptionsIconPosition);
      window.removeEventListener("scroll", updateOptionsIconPosition);
    };
  }, [shouldApplySmallStyling]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    const optionsElement = document.querySelector(
      '[data-options-menu="true"]'
    ) as HTMLElement;
    setAnchorEl(optionsElement || event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogin = () => {
    // This now only handles Firebase login since GitHub has its own menu item
    handleClose();
    setTimeout(() => {
      const firebaseButton = firebaseAuthRef.current?.querySelector("button");
      if (firebaseButton) {
        firebaseButton.click();
      }
    }, 100);
  };

  const handleFirebaseLogout = async () => {
    try {
      await signOutUser();
      handleClose();
    } catch (error) {
      console.error("Firebase logout error:", error);
    }
  };

  const handleAccountSettings = () => {
    // For now, just show user info - could expand to a full settings dialog
    alert(
      `Account: ${
        firebaseUser?.displayName || firebaseUser?.email || "User"
      }\nEmail: ${firebaseUser?.email || "No email"}`
    );
    handleClose();
  };

  const handleLogout = async () => {
    await invalidateToken();
    localStorage.clear();
    sessionStorage.clear();
    window.location.assign("/");
    setLoggedIn(false);
  };

  const handleResetCache = async () => {
    handleClose();
    localStorage.clear();
    sessionStorage.clear();
    // Clear CodeCanvas Cache
    clear();
    // Clear Drawio's Cache
    postToDrawioWaitForResponse({
      action: "RESET_CACHE",
    })
      .then(() => {
        // Reload the page to clear the cache, add reloadTime param to prevent browser's auto caching
        let url = new URL(window.location.href);
        // Remove all params
        url = new URL(url.origin + url.pathname);
        url.searchParams.set("reloadTime", Date.now().toString());
        window.location.href = url.toString();
      })
      .catch(function (error) {
        console.error(error);
        setErrorNotification("Could not reset cache.");
      });
  };

  const handleDeleteDiagramFile = async () => {
    handleClose();

    const response = await deleteDiagramFile(
      useStore.getState().currentRepo,
      useStore.getState().currentBranch,
      useStore.getState().currentRepoMetadata.owner
    );

    if (response.status === 200) {
      handleResetCache();
    } else {
      setErrorNotification("Could not remove diagram file.");
    }
  };

  const handleEnableCodeCanvasScanner = async () => {
    handleClose();

    if (!isLoggedIn) {
      sessionStorage.setItem(
        "redirectUrl",
        window.location.href.replace(
          "session=unauthenticatedGithub&",
          "session=github&"
        )
      );
      login();

      return;
    }

    // saveFileToGithub
    const workflowFileContent = await fetch(workflowFile).then((r) => r.text());
    const response = await saveFileToGithub(
      useStore.getState().currentRepo,
      useStore.getState().currentBranch,
      workflowFileContent,
      useStore.getState().currentRepoMetadata.owner,
      ".github/workflows/codecanvas.yml"
    );

    if (response.status === 200 || response.status === 201) {
      setDoesCodeCanvasWorkflowFileExist(true);
      setSuccessNotification("CodeCanvas Scanner enabled successfully!");
    } else {
      setErrorNotification(
        "Could not enable CodeCanvas Scanner. Please report this issue on Discord."
      );
    }
  };

  const learnMoreAboutTelemetryData = () => {
    window.open(
      `https://www.notion.so/Getting-Started-8aefb25ae4c74c269540ec5be7f014ea?pvs=4#b339910856fb447b941a3ce822040c44`,
      "_blank"
    );
  };

  // Removes the reloadTime param from the URL after the component is mounted
  useEffect(() => {
    removeURLParam(window, "reloadTime");
  }, []);

  // Check if workflow file exists in github repoData
  useEffect(() => {
    if (".github/workflows/codecanvas.yml" in repoData) {
      setDoesCodeCanvasWorkflowFileExist(true);
    } else {
      setDoesCodeCanvasWorkflowFileExist(false);
    }
  }, [repoData]);

  const handleTelemetryData = (e) => {
    if (telemetryData) {
      const TelemetryDialogMessage = () => {
        return (
          <div>
            Telemetry data are debug messages and analytics sent to us to help
            debug and improve the product faster. Learn more about telemetry
            data{" "}
            <a
              href="https://www.notion.so/Getting-Started-8aefb25ae4c74c269540ec5be7f014ea?pvs=4#b339910856fb447b941a3ce822040c44"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: "normal" }}
            >
              here
            </a>
            . Are you sure you want to disable telemetry data?
          </div>
        );
      };
      useStore.getState().setDialog("WARNING_DIALOG", {
        message: <TelemetryDialogMessage />,
        button1Function: () => {
          localStorage.setItem("telemetryData", "false");
          setTelemetryData(false);
          window.location.reload();
        },
      });
    } else {
      localStorage.setItem("telemetryData", "true");
      setTelemetryData(true);
      // wait 300ms for the user to notice the switch flipped
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  };

  const handleStatsWidget = () => {
    setStatsWidgetVisible(!statsWidgetVisible);
  };

  return (
    <>
      {/* Placeholder div to maintain position in Header */}
      <div
        ref={optionsIconRef}
        style={{
          display: "flex",
          alignItems: "center",
          height: "28px",
          marginRight: "10px",
          width: "30px",
        }}
      >
        <div style={{ width: "30px", height: "30px" }} />
      </div>

      {/* Portal renders the actual icon outside of Header's DOM hierarchy */}
      {!shouldHideButton &&
        createPortal(
          <div
            data-testid="optionsMenuButton"
            style={{
              position: "fixed",
              top: shouldApplySmallStyling
                ? `${optionsIconPosition.top - 8}px`
                : `${optionsIconPosition.top}px`,
              right: shouldApplySmallStyling
                ? "41px"
                : `${optionsIconPosition.right}px`,
              zIndex: 9999997, // Below help icon and discord but above everything else
              display: "flex",
              alignItems: "center",
              height: shouldApplySmallStyling ? "24px" : "28px",
              transition: "all 0.2s ease",
            }}
          >
            <IconButton
              sx={{
                borderRadius: "30px",
                height: shouldApplySmallStyling ? "24px" : "30px",
                width: shouldApplySmallStyling ? "24px" : "30px",
                padding: shouldApplySmallStyling
                  ? "2px"
                  : firebaseUser
                  ? "2px"
                  : "3px",
                transition: "all 0.2s ease",
              }}
              id="OptionsMenuButton"
              data-options-menu="true"
              aria-controls={open ? "Options Menu" : undefined}
              onClick={handleClick}
              aria-label="Options Menu"
              component="label"
              color="primary"
            >
              {firebaseUser ? (
                <Avatar
                  src={firebaseUser.photoURL || undefined}
                  sx={{
                    width: shouldApplySmallStyling ? 22 : 26,
                    height: shouldApplySmallStyling ? 22 : 26,
                    bgcolor: theme.custom.pink,
                    fontSize: shouldApplySmallStyling ? "10px" : "12px",
                    transition: "all 0.2s ease",
                  }}
                >
                  {firebaseUser.displayName?.charAt(0) ||
                    firebaseUser.email?.charAt(0) ||
                    "U"}
                </Avatar>
              ) : (
                <MoreVertIcon
                  sx={{
                    fontSize: shouldApplySmallStyling ? "22px" : "24px",
                    transition: "font-size 0.2s ease",
                  }}
                />
              )}
            </IconButton>
          </div>,
          document.body
        )}

      <StyledMenu
        id="OptionsMenu"
        MenuListProps={{
          "aria-labelledby": "OptionsMenuButton",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {firebaseUser ? (
          <>
            <MenuItem onClick={handleAccountSettings} disableRipple>
              <SettingsIcon />
              Account Settings
            </MenuItem>
            <MenuItem onClick={handleFirebaseLogout} disableRipple>
              <LogoutIcon />
              Sign Out
            </MenuItem>
          </>
        ) : (
          <MenuItem onClick={handleLogin} disableRipple>
            <PersonIcon />
            Login
          </MenuItem>
        )}

        {/* GitHub Login/Logout - separate from Firebase */}
        {isLoggedIn && (
          <MenuItem onClick={handleLogout} disableRipple>
            <GitHubIcon />
            Logout from GitHub
          </MenuItem>
        )}
        <MenuItem onClick={handleResetCache} disableRipple>
          <RestartAltIcon />
          Reset Cache
        </MenuItem>
        {debugMode && (
          <MenuItem onClick={handleDeleteDiagramFile} disableRipple>
            <DeleteOutlineRoundedIcon />
            Delete Diagram File
          </MenuItem>
        )}
        {!doesCodeCanvasWorkflowFileExist && (
          <MenuItem onClick={handleEnableCodeCanvasScanner} disableRipple>
            <RadarIcon />
            Enable CodeCanvas Scanner
          </MenuItem>
        )}
        <MenuItem
          disableGutters
          disableTouchRipple
          disableRipple
          style={{ paddingLeft: "10px", cursor: "default" }}
        >
          <Switch
            checked={statsWidgetVisible}
            onClick={handleStatsWidget}
            size="small"
            data-testid="statsWidgetSwitch"
          />
          <BarChartIcon
            style={{ marginLeft: "10px", marginRight: "10px" }}
            fontSize="small"
          />
          <div style={{ position: "relative" }}>Stats for nerds</div>
        </MenuItem>
        <MenuItem
          disableGutters
          disableTouchRipple
          disableRipple
          style={{ paddingLeft: "10px", cursor: "default" }}
        >
          <Switch
            defaultChecked
            checked={telemetryData}
            onClick={handleTelemetryData}
            size="small"
            data-testid="telemetryDataSwitch"
          />
          <div style={{ position: "relative", paddingLeft: "10px" }}>
            Telemetry Data
          </div>
          <HelpOutlineSharpIcon
            className="HelpIcon"
            color="primary"
            onClick={() => learnMoreAboutTelemetryData()}
            style={{ paddingLeft: "10px" }}
          />
        </MenuItem>
      </StyledMenu>

      {/* Firebase Auth Component */}
      <div
        ref={firebaseAuthRef}
        style={{ position: "absolute", top: "-9999px", left: "-9999px" }}
      >
        <FirebaseAuth />
      </div>
    </>
  );
}
